import { useEffect, useState } from 'react';

import { Button, Dialog, Icon, Text, Tooltip } from '@ui-component-library';

/** Keys match `SALE_OPTIONS[].value` in sellTimberShared */
export const SELL_TIMBER_SALE_INFO = {
  avverkningsuppdrag: {
    title: 'Avverkningsuppdrag',
    text: 'Du får ersättning för virket enligt överenskommen prislista eller avtal, ofta per sortiment och kvalité minus kostnaden för avverkningen.'
  },
  leveransrotköp: {
    title: 'Leveransrotköp',
    text: 'Med denna försäljningsform får du ett nettopris inkl. avverkningskostnader oavsett kvalité och sortiment. Exempel: 450kr/ m³fub.'
  },
  leveransvirke: {
    title: 'Leveransvirke',
    text: 'När du eller någon annan avverkar och skogsbolaget köper virket vid bilväg. Här får du också oftast prislista plus eventuella tillägg, alternativt fast pris per sortiment.'
  },
  rotpost: {
    title: 'Rotpost',
    text: 'Skogen mäts upp innan avverkning och säljs för ett bestämt totalpris. Köparen ansvarar sedan för avverkning och transport.'
  }
};

const SELL_TIMBER_SALE_INFO_KEYS = [
  'avverkningsuppdrag',
  'leveransrotköp',
  'leveransvirke',
  'rotpost'
];

/** Help texts for Sell Timber form fields (Swedish). */
export const SELL_TIMBER_FIELD_INFO = {
  tillDate:
    'Här väljer du hur lång tid din annons ska vara publicerad hos Treebula. Det är den tid som virkesköparna har på sig att lämna anbud. Vi rekommenderar att annonsen ligger publicerad i 3 till 4 veckor så att köparna har tillräckligt med tid.',

  hurSaljaVirke: SELL_TIMBER_SALE_INFO_KEYS.map((key) => {
    const { title, text } = SELL_TIMBER_SALE_INFO[key];
    return `${title}: ${text}`;
  }).join('\n\n'),

  typAvAtgard: `Slutavverkning
Den avslutande skörden när skogen är fullvuxen. Merparten av träden fälls. Därefter planteras ny skog.

Gallring
En skötselåtgärd i växande skog där man plockar bort svagare träd. Virket från gallring säljs oftast som massaved.

Plockhuggning
En skötselåtgärd där enskilda träd plockas ut ur beståndet. Virket säljs oftast som massaved.

Biobränsle
Består främst av grenar, toppar (grot) och skadat virke som blir över vid annan avverkning.

Övrig avverkning
Ett samlingsbegrepp för virke som fälls av andra skäl än planerad skötsel.`,

  ovrigInformation:
    'Här anger du all övrig information som är bra för köparen att känna till. Detta kan exempelvis vara vilka bilvägar man får köra på, kod till vägbom, om du önskar förskott, om skogen är snitslad/bandad eller annan viktig information som hjälper köparen. Om det inte finns någon övrig information så lämna denna ruta tom.',

  bilagor:
    'Här kan du ladda upp samtliga bilagor som rör den aktuella åtgärden. Exempelvis skogsbruksplan, karta, villkor, stämplingslängder, avverkningsanmälan etc.',

  step3VirkesData:
    'Notera att volym och trädslag behöver inte vara 100% rätt för din annons, utan säkerställ bara att det stämmer ganska bra.'
};

/** Grot wizard step 3 — keys used in {@link SellTimberGrotStep3}. */
export const SELL_TIMBER_GROT_STEP3_FIELD_INFO = {
  grot_volume: {
    title: 'Volym biobränsle',
    text: 'Ange den uppskattade volymen biobränsle i kubikmeter (m³). Det behöver inte vara exakt.'
  },
  grot_heap_covered: {
    title: 'Biobränslet täckt',
    text: 'Ange om högen med biobränsle är täckt, till exempel med presenning, plast eller liknande, för att skydda biobränslet från fukt.'
  },
  grot_collected: {
    title: 'Biobränslet uppsamlat',
    text: 'Ange om biobränslet redan är uppsamlat på platsen. till exempel i en hög, på en upplagsplats eller om det fortfarande ligger kvar i skogen.'
  }
};

/** Keys match `ACTION_OPTIONS[].value` in sellTimberShared */
export const SELL_TIMBER_ACTION_INFO = {
  slutavverkning: {
    title: 'Slutavverkning',
    text: 'Den avslutande skörden när skogen är fullvuxen. Merparten av träden fälls. Därefter planteras ny skog.'
  },
  gallring: {
    title: 'Gallring',
    text: 'En åtgärd i växande skog där vissa träd tas bort för att ge de kvarvarande träden mer plats och bättre utveckling.'
  },
  plockhuggning: {
    title: 'Plockhuggning',
    text: 'En åtgärd där enskilda träd plockas ut ur beståndet, till exempel för att ta bort skadade eller underpresterande träd.'
  },
  grot: {
    title: 'Biobränsle',
    text: 'Består främst av grenar och toppar som blir kvar efter avverkning. Materialet kan samlas in och användas som skogsbränsle.'
  },
  vindfallen: {
    title: 'Övrig avverkning',
    text: 'Avverkning som görs av andra skäl än planerad gallring eller slutavverkning, till exempel efter storm, snöbrott eller andra skador.'
  }
};

const MOBILE_MAX_WIDTH_PX = 768;

/**
 * Info icon: same pattern as PricingPageNew — on small screens tap opens a Dialog; on md+ hover Tooltip (like the desktop comparison table).
 */
export function SellTimberFieldInfo({
  text,
  title,
  'aria-label': ariaLabel = 'Mer information',
  side = 'right'
}) {
  const dialogTitle = title ?? ariaLabel;
  const [dialogOpen, setDialogOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== 'undefined' && window.innerWidth < MOBILE_MAX_WIDTH_PX
  );

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < MOBILE_MAX_WIDTH_PX);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (!isMobile) {
      setDialogOpen(false);
    }
  }, [isMobile]);

  /** Inline styles: tooltip is portaled to document.body — Tailwind on children may not apply; pre-wrap + scroll here always work */
  const tooltipBody = (
    <div
      style={{
        whiteSpace: 'pre-wrap',
        textAlign: 'left',
        maxHeight: 'min(70vh, 520px)',
      }}
    >
      {text}
    </div>
  );

  const mobileTrigger = (
    <button
      type="button"
      className="inline-flex shrink-0 rounded-full bg-[#E8E8E8] p-0.5"
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        setDialogOpen(true);
      }}
      aria-label={ariaLabel}
    >
      <Icon name="InfoOutlined" size="small" className="fill-[#737373]" />
    </button>
  );

  const desktopTrigger = (
    <Tooltip
      content={tooltipBody}
      aria-label={ariaLabel}
      side={side}
      className="inline-flex shrink-0 align-middle"
      tabIndex={0}
    >
      <span className="inline-flex cursor-help items-center rounded-full p-0.5">
        <Icon name="InfoOutlined" size="medium" className="fill-[#1A1A1A]" />
      </span>
    </Tooltip>
  );

  return (
    <>
      {isMobile ? mobileTrigger : desktopTrigger}

      {isMobile ? (
        <Dialog isOpen={dialogOpen} onClose={() => setDialogOpen(false)} size="small">
          <Dialog.Header className="!flex flex-row items-center justify-between gap-3">
            <Text variant="text-sm/leading-normal/normal" className="min-w-0 flex-1 text-left text-[#333333]">
              {dialogTitle}
            </Text>
            <Button
              variant="outline"
              size="icon"
              type="button"
              onClick={() => setDialogOpen(false)}
              aria-label="Stäng"
            >
              <Icon name="CrossSimple" className="color-charcoal-500" size="medium" />
            </Button>
          </Dialog.Header>
          <Dialog.Content>
            <Text variant="text-sm/leading-normal/normal" className="whitespace-pre-wrap text-[#333333]">
              {text}
            </Text>
          </Dialog.Content>
        </Dialog>
      ) : null}
    </>
  );
}
